import * as React from 'react';
import clsx from 'clsx';
import { Stack } from '@mui/material';
import styles from './Chat.module.css';
import Markdown from 'react-markdown';
import { LinkElement } from './LinkElement';
import { Pins } from './Pins/Pins';
import { parseItinerary } from './util';
import { SaveItineraryButton } from './SaveItineraryButton';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { MenuItems } from './MenuItems/MenuItems';
import { useTranslation } from 'react-i18next';
import { useMapDisplayContext } from 'components/Map/MapDisplayContext';
import { CreateItineraryFromPinsButton } from './CreateItineraryFromPinsButton';

interface Props {
  text: string;
  isUser: boolean;
  onCloseChat: () => void;
}

const preprocessText = (text: string): string => {
  // Replace <button onclick="getDirections('xyz')">Text</button>
  // with [Text](/button?action=getDirections&pinKey=xyz)
  return text.replace(
    /<button onclick="getDirections\('([^']+)'\)">([^<]+)<\/button>(?:\s*\.)?/g,
    (_, pinKey, text) => `[${text}](/button?action=getDirections&pinKey=${pinKey})`
  );
};

const collectPinKeys = (text: string): string[] => {
  const pinKeys = new Set<string>();

  // Collect from getDirections buttons
  const buttonRegex = /<button onclick="getDirections\('([^']+)'\)">([^<]+)<\/button>/g;
  let buttonMatch;
  while ((buttonMatch = buttonRegex.exec(text)) !== null) {
    pinKeys.add(buttonMatch[1]);
  }

  // Collect from markdown links
  const urlRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let linkMatch;
  while ((linkMatch = urlRegex.exec(text)) !== null) {
    try {
      const url = linkMatch[2];
      const urlObj = new URL(url);
      const pinKey = urlObj.searchParams.get('pin');
      if (pinKey) {
        pinKeys.add(pinKey);
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }

  return Array.from(pinKeys);
};

const collectMenuItemKeys = (text: string): string[] => {
  const menuItemKeys = new Set<string>();

  // Collect from markdown links
  const urlRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let linkMatch;
  while ((linkMatch = urlRegex.exec(text)) !== null) {
    try {
      const url = linkMatch[2];
      const urlObj = new URL(url);
      const menuItemKey = urlObj.searchParams.get('menu_item');
      if (menuItemKey) {
        menuItemKeys.add(menuItemKey);
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }

  return Array.from(menuItemKeys);
};

export const Message = React.forwardRef<HTMLDivElement, Props>(
  ({ text, isUser, onCloseChat }, ref) => {
    const { t } = useTranslation();
    const {
      replaceUrlParams,
      setActiveRestaurantId,
      setActiveItemSelection,
      setActiveRestaurantPageStack,
    } = useMapDisplayContext();
    const digitalMap = useSelector(selectDigitalMap);
    const processedText = preprocessText(text);
    const pinKeys = collectPinKeys(text);
    const menuItemKeys = collectMenuItemKeys(text);

    const itinerary = parseItinerary(processedText);

    const restaurantPin =
      pinKeys.length === 1 ? digitalMap?.pins?.find((pin) => pin.key === pinKeys[0]) : null;
    const restaurantId =
      restaurantPin?.online_order_restaurant_id || restaurantPin?.view_menu_restaurant_id;
    const restaurant = restaurantId
      ? digitalMap?.restaurants.find((r) => r.id === restaurantId)
      : null;

    const restaurantMenuItems =
      restaurant?.menu?.menu_items.filter(
        (item) => menuItemKeys.length === 0 || menuItemKeys.includes(item.key)
      ) ?? [];

    return (
      <Stack
        ref={ref}
        direction="row"
        spacing={1}
        className={isUser ? styles.userMessage : styles.botMessage}
        justifyContent={isUser ? 'flex-end' : 'flex-start'}
        alignItems="flex-start"
      >
        <div
          className={clsx(
            styles.messageContent,
            isUser ? styles.userMessageContent : styles.botMessageContent
          )}
        >
          <Markdown
            components={{
              a: ({ href, children }) => (
                <LinkElement url={href || ''} onCloseChat={onCloseChat}>
                  {children}
                </LinkElement>
              ),
            }}
          >
            {processedText}
          </Markdown>
          {restaurantMenuItems.length > 0 && (
            <MenuItems
              items={restaurantMenuItems}
              onClickItem={(item) => {
                replaceUrlParams({
                  pin: restaurantPin?.key,
                  pind: 'DETAILS',
                });
                setActiveRestaurantId(restaurantId ?? null);
                setActiveItemSelection({
                  menuItemKey: item.key,
                  optionGroups: [],
                  quantity: 1,
                });
                setActiveRestaurantPageStack(['MAIN', 'ITEM']);
                onCloseChat();
              }}
              id="restaurant-menu-items"
              title={t('Menu Recommendations')}
            />
          )}
          {itinerary && <SaveItineraryButton itinerary={itinerary} onCloseChat={onCloseChat} />}
          {pinKeys.length > 0 && <Pins pinKeys={pinKeys} onCloseChat={onCloseChat} />}
          {pinKeys.length > 2 && (
            <CreateItineraryFromPinsButton pinKeys={pinKeys} onCloseChat={onCloseChat} />
          )}
        </div>
      </Stack>
    );
  }
);

Message.displayName = 'Message';
